<template>
  <div class="w-full flex flex-col gap-9">
    <div class="flex flex-col gap-6 w-full pt-8 lg:pt-0">
      <div class="flex flex-col lg:flex-row items-start w-full px-1 py-1 lg:px-0 lg:py-0">
        <div class="flex items-center gap-2 flex-1">
          <h1 class="text-text-primary text-3xl font-semibold">{{ $t('common.statistics') }}</h1>
          <div class="has-tooltip">
            <img src="../../../../assets/icons/info-icon.svg" :alt="$t('coach.alt.avatar')" class="w-6 h-6 cursor-pointer" />
            <div class="tooltip bg-gray-900 rounded-lg p-2 ml-8 -mt-7">
              <p class="text-text-secondary text-xs">{{ $t('coach.match_statistics.info_description', { name: playerName(props.selectedUser), rank: rankComparisonLabel }) }}</p>
            </div>
          </div>
        </div>

        <!-- dropdown menu -->
        <Menu as="div" class="pt-2 relative inline-block">
          <div>
            <MenuButton
              class="group rounded-md bg-gray-800 px-3.5 py-2 w-full lg:w-[240px] text-center text-sm font-medium text-white hover:outline hover:outline-gray-500 focus:outline-outline focus:outline-gray-500"
            >
              <span class="flex w-full items-center justify-between">
                <span class="flex min-w-0 items-center justify-between space-x-3">
                  <img class="w-8 h-8 bg-red-500 rounded-full" :src="imageURL()" />
                  <span class="flex min-w-0 flex-1 flex-col">
                    {{ playerName(props.selectedUser) }}
                  </span>
                </span>
                <ChevronDownIcon
                  class="h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                  aria-hidden="true"
                />
              </span>
            </MenuButton>
          </div>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full lg:w-[250px] origin-top divide-y divide-gray-200 rounded-md bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1">
                <MenuItem v-for="(user, index) in users" :key="user">
                  <div>
                    <a
                      @click="updateSelectedUser(user)"
                      :class="[
                        'flex gap-2 px-4 py-2 text-sm text-text-primary cursor-pointer hover:bg-gray-800 duration-300 items-center',
                      ]"
                    >
                      <img
                        class="w-8 h-8 bg-red-500 rounded-full"
                        :src="user.avatar"
                      />
                      <h1 class="flex-1">{{ playerName(user) }}</h1>
                      <CheckIcon
                        v-if="
                          props.selectedUser?.steam_id === user?.steam_id
                        "
                        class="h-5 w-5 text-primary-500"
                      />
                    </a>
                    <div
                      v-if="
                        index < users.length - 1
                      "
                      class="border-b border-gray-800 mt-1 mb-1"
                    />
                  </div>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>

      <div class="flex flex-col gap-16 w-full">
        <div class="flex flex-col gap-4" v-if="matchPlatform !== 'refrag' && matchPlatform !== 'pro'">
          <div class="flex gap-2 items-center px-1 py-1 lg:px-0 lg:py-0">
            <h1 class="text-text-primary text-2xl font-semibold flex">{{ $t('coach.shared.weaknesses') }}</h1>
            <span
              class="w-6 h-6 flex items-center justify-center text-danger-400 bg-danger-900 rounded-md"
            >{{ Math.min(negativeStats.length, 4) }}</span
            >
          </div>

          <div
            v-if="orderedStats && negativeStats.length > 0"
            class="grid grid-cols-12 gap-6 items-center items-stretch"
          >
            <div class="flex flex-col gap-3 flex-1 col-span-12 lg:col-span-4">
              <div
                v-for="stat in negativeStats.slice(0, 4)"
                class="flex p-4 rounded-lg bg-gray-900 items-center cursor-pointer hover:bg-gray-800 duration-300"
                :class="[
                  props.selectedStat?.key === stat.key
                    ? 'border border-primary-500'
                    : ' border border-gray-900',
                ]"
                @click="updateSelectedStat(stat)"
              >
                <h2 class="text-text-secondary flex-1">{{ stat?.friendly_title }}</h2>
                <h1 class="text-text-primary text-lg font-medium">
                  {{ formatStatString(stat.value, stat.unit_type) }}
                </h1>
              </div>
            </div>

            <div v-if="props.selectedStat" class="flex flex-col lg:flex-row col-span-12 lg:col-span-8 rounded-lg bg-gray-900">
              <div class="flex flex-1 flex-col gap-4">
                <div class="flex gap-4 pt-6 px-6 items-center">
                  <div class="flex flex-col gap-1 flex-1">
                    <h2 class="text-text-secondary text-sm">
                      {{ props.selectedStat?.friendly_title }}
                    </h2>
                    <h1 class="text-text-primary text-lg font-medium">
                      {{
                        formatStatString(
                          props.selectedStat.value,
                          props.selectedStat.unit_type,
                        )
                      }}
                    </h1>
                  </div>
                  <div class="flex gap-1">
                    <h1 class="font-medium text-danger-400 flex">
                      <span v-if="getOptimalSortDirection(props.selectedStat.key) == 'asc'">+</span>
                      <span v-else>-</span>
                      <span>
                        {{
                          formatStatString(
                            props.selectedStat.difference,
                            props.selectedStat.unit_type,
                          )
                        }}
                      </span>
                    </h1>
                  </div>
                </div>

                <div class="w-full px-6">
                  <div class="w-full h-px bg-gray-800" />
                </div>

                <i18n-t scope="global" keypath="coach.comparison.other_match" tag="div" class="text-sm text-text-secondary px-6 pb-2 flex-1">
                  <template #name>{{ playerName(props.selectedUser) }}</template>
                  <template #stat>{{ props.selectedStat.friendly_title || toTitleCase(props.selectedStat.title) }}</template>
                  <template #delta>
                    <span class="text-danger-400">{{ $t('coach.comparison.delta_worse', { pct: Math.abs(parseFloat(parseFloat(props.selectedStat.percentDifference).toFixed(2))) }) }}</span>
                  </template>
                </i18n-t>

                <button
                  v-if="selectedUser?.player?.steam_id === user?.steam_id"
                  @click="updateServerModal()"
                  class="py-2.5 mx-6 mb-6 rounded-md bg-primary-600 text-text-primary text-xs z-50 cursor-pointer hover:bg-primary-500 duration-300"
                >
                  {{ $t('coach.shared.improve_now') }}
                </button>
              </div>

              <div class="flex-1 flex items-stretch p-6">
                <div
                  class="flex-1 bg-gray-800 rounded-lg border-gray-700 border p-5 flex flex-col"
                >
                  <div class="flex-1 flex gap-8">
                    <div class="flex flex-col gap-1">
                      <div class="flex gap-1 items-center">
                        <div class="h-2 w-2 rounded-sm bg-primary-500" />
                        <h2 class="text-text-secondary text-xs">
                          {{ $t('coach.match_statistics.player_average', { name: playerName(props.selectedUser) }) }}
                        </h2>
                      </div>
                      <h1 class="text-text-primary text-lg font-medium">
                        {{
                          formatStatString(
                            props.userData[selectedStat.title]?.value,
                            selectedStat.unit_type,
                          )
                        }}
                      </h1>
                    </div>
                    <div class="flex flex-col gap-1">
                      <div class="flex gap-1 items-center">
                        <div class="h-2 w-2 rounded-sm bg-secondary-500" />
                        <h2 class="text-text-secondary text-xs">{{ $t('coach.shared.target_average') }}</h2>
                      </div>
                      <h1 class="text-text-primary text-lg font-medium">
                        {{
                          formatStatString(
                            props.userData[selectedStat.title]
                              ?.avg,
                            selectedStat.unit_type,
                          )
                        }}
                      </h1>
                    </div>
                  </div>
                  <LineChart
                    v-if="props.userMatchHistory"
                    :options="options"
                    :chartData="getChartData()"
                    class="w-full max-h-[112px]"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="orderedStats && negativeStats.length === 0"
            class="flex col-span-8 rounded-lg bg-gray-900"
          >
            <div v-if="rankComparisonUnavailable" class="flex flex-col gap-4 p-6">
              <h1 class="text-text-primary text-lg font-medium">{{ $t('coach.shared.comparison_unavailable_title') }}</h1>
              <p class="text-text-secondary text-sm">
                {{ $t('coach.match_statistics.comparison_unavailable_body', { name: playerName(props.selectedUser), rank: rankComparisonLabel }) }}
              </p>
            </div>
            <div v-else class="flex flex-col gap-4 p-6">
              <h1 class="text-text-primary text-lg font-medium">{{ $t('coach.shared.no_weaknesses_title') }}</h1>
              <p class="text-text-secondary text-sm">
                {{ $t('coach.match_statistics.no_weaknesses_body', { name: playerName(props.selectedUser) }) }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-4" v-if="matchPlatform !== 'refrag' && matchPlatform !== 'pro'">
          <div class="flex gap-2 items-center px-1 py-1 lg:px-0 lg:py-0">
            <h1 class="text-text-primary text-2xl font-semibold flex">{{ $t('coach.shared.strengths') }}</h1>
            <span
              class="w-6 h-6 flex items-center justify-center text-success-400 bg-success-900 rounded-md"
            >{{ Math.min(positiveStats.length, 3) }}</span
            >
          </div>

          <div v-if="positiveStats?.length > 0" class="grid grid-cols-1 lg:grid-cols-3 w-full gap-6">
            <StatBlock
              v-for="stat in positiveStats.slice(0, 3)"
              :key="stat.key"
              :stat="stat"
              :style="'strength'"
              :show-improve-button="false"
              :player-name="playerName(props.selectedUser)"
            />
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div class="flex gap-2 items-center px-1 py-1 lg:px-0 lg:py-0">
            <h1 class="text-text-primary text-2xl font-semibold flex">{{ $t('coach.shared.all_statistics') }}</h1>
          </div>

          <div class="grid lg:grid-cols-2 w-full gap-6">
            <HitgroupPanel v-if="hasHitgroupData" :values="props.userData" class="order-2" />
            <div
              v-for="(statSection, sectionIndex) in STAT_SECTIONS"
              :key="statSection.name"
              class="flex flex-col divide-y divide-gray-950 bg-gray-900 rounded-lg"
              :class="['order-1', 'order-3', 'order-4', 'order-5'][sectionIndex]"
            >
              <div class="px-5 py-4 lg:px-4 lg:py-3 text-text-primary">{{ $t(statSection.name) }}</div>
              <div
                v-for="stat in statSection.stats"
                :key="stat.key"
                class="p-4 flex gap-2 items-center"
              >
                <h2 class="text-text-secondary flex-1">{{ stat.key === 'adr' ? 'ADR' : $t(`coach.stat_labels.${stat.key}`) }}</h2>
                <h1
                  v-if="thinSamples[stat.key]"
                  class="text-text-secondary text-lg font-medium"
                  :title="thinSamples[stat.key].title"
                >
                  {{ thinSamples[stat.key].text }}
                </h1>
                <h1 v-else :class="`${getStatColorClassForStat(stat.key)} text-lg font-medium`">
                  {{ formatStatValue(stat) }}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, reactive, defineEmits, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { Chart, registerables } from 'chart.js';
import { LineChart } from 'vue-chart-3';
import {
  Menu, MenuButton, MenuItem, MenuItems,
} from '@headlessui/vue';
import { ChevronDownIcon, CheckIcon } from '@heroicons/vue/20/solid';
import axios from 'axios';
import { useRouter, useRoute } from 'vue-router';
import unknownUserImage from '../../../../assets/unknown-user.png';
import StatBlock from '../filters/StatBlock.vue';
import { createClickablePointsPlugin } from '../../../../utils/chartPlugins.js';
import { matchPlatformOf, rankComparisonPlatformLabel } from '../../../../utils/matchPlatform.js';
import { statFriendlyName } from '../../../../utils/coach_utils.js';
import { compareStatToAverage, hasRankAverage } from '../../../../utils/stat_comparison.js';
import HitgroupPanel from '../../HitgroupPanel.vue';
import { buildHitgroupData } from '../../../../utils/hitgroups.js';
import { isWorseWhenHigher as isWorseWhenHigherStat } from '../../../../utils/stat_directions.js';
import { fetchStatDefinitions, useStatDefinitions } from '../../../../composables/useStatBreakdown.js';
import { sampleState } from '../../../../utils/stat_sample.js';

// Section names are coach.stat_groups keys; row labels are coach.stat_labels.<key> (ADR stays literal).
const STAT_SECTIONS = [
  {
    name: 'coach.stat_groups.general',
    stats: [
      { key: 'kills', formatter: 'integer' },
      { key: 'assists', formatter: 'integer' },
      { key: 'deaths', formatter: 'integer' },
      { key: 'kdr', formatter: 'decimal' },
      { key: 'headshot_kill_percentage', formatter: 'percentage', multiplier: 100 },
      { key: 'adr', formatter: 'integer' },
      { key: 'hltv_rating', formatter: 'decimal', decimalPlaces: 2 },
    ],
  },
  {
    name: 'coach.stat_groups.aim',
    stats: [
      { key: 'time_to_kill_average', formatter: 'seconds', unit: 's' },
      { key: 'time_to_damage_average', formatter: 'seconds', unit: 's' },
      { key: 'spotted_hit_rate', formatter: 'percentage', multiplier: 100 },
      { key: 'headshot_percentage', formatter: 'percentage', multiplier: 100 },
      { key: 'accurate_shots_while_counterstrafing_rate', formatter: 'percentage', multiplier: 100 },
      { key: 'shots_at_low_velocity_rate', formatter: 'percentage', multiplier: 100 },
      { key: 'velocity_control_rate', formatter: 'percentage', multiplier: 100 },
      {
        key: 'crosshair_placement_average', formatter: 'decimal', decimalPlaces: 2, unit: '°',
      },
      { key: 'spray_shot_accuracy', formatter: 'percentage', multiplier: 100 },
    ],
  },
  {
    name: 'coach.stat_groups.entry_trades',
    stats: [
      { key: 'opening_successes', formatter: 'integer' },
      { key: 'opening_fails', formatter: 'integer' },
      { key: 'trade_kill_successes', formatter: 'integer' },
      { key: 'trade_kill_fails', formatter: 'integer' },
      { key: 'traded_death_successes', formatter: 'integer' },
      { key: 'traded_death_fails', formatter: 'integer' },
    ],
  },
  {
    name: 'coach.stat_groups.utility',
    stats: [
      { key: 'util_flash_assists', formatter: 'integer' },
      { key: 'util_flash_enemies_flashed', formatter: 'integer' },
      { key: 'util_flash_friends_flashed', formatter: 'integer' },
      { key: 'average_enemy_flash_time', formatter: 'seconds', unit: 's' },
      { key: 'util_enemy_damage', formatter: 'integer' },
      { key: 'util_lost_cost', formatter: 'currency', prefix: '$' },
    ],
  },
];
// Create the plugin
const clickablePointsPlugin = createClickablePointsPlugin(
  (index) => props.userMatchHistory[props.userMatchHistory.length - 1 - index]?.match_id,
  (matchId) => router.push(`/matches/${matchId}`),
);

// Register the plugin
Chart.register(...registerables, clickablePointsPlugin);

const auth = useAuth();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const user = ref(auth.user());
const mappedStats = ref([]);
const statAverage = ref(0);

const props = defineProps({
  matchData: {
    type: Object,
    required: true,
  },
  userData: Object,
  selectedUser: Object,
  selectedStat: Object,
  userMatchHistory: Array,
});

const hasHitgroupData = computed(() => buildHitgroupData(props.userData) !== null);

// wi_0fc147fe-ef43-46b8-9e3b-5e2c8e90f70c: min_sample floors. While null (loading or
// failed), no stat is treated as thin and the tab renders as before.
const definitions = useStatDefinitions();
// True once the floors are known or known to be unavailable; the auto-select
// below waits for it, or a thin stat is selected and then hidden.
const definitionsSettled = ref(definitions.value !== null);
if (!definitionsSettled.value) {
  fetchStatDefinitions().catch(() => {}).finally(() => { definitionsSettled.value = true; });
}

// All Statistics rows of the selected player whose rate rests on too few attempts.
const thinSamples = computed(() => {
  const thin = {};
  STAT_SECTIONS.forEach((statSection) => statSection.stats.forEach(({ key }) => {
    const sample = sampleState(key, props.selectedUser, definitions.value);
    if (!sample?.belowMin) return;
    const recipe = definitions.value[key];
    const label = recipe.inputs?.find((input) => input.role === 'denominator')?.label ?? recipe.sample_key;
    thin[key] = {
      text: sample.count
        ? t('coach.match_statistics.too_few_attempts', { n: sample.count })
        : t('coach.match_statistics.no_attempts'),
      title: t('coach.match_statistics.too_few_attempts_title', { min: sample.minSample, label: label.toLowerCase() }),
    };
  }));
  return thin;
});

const matchPlatform = computed(() => matchPlatformOf(props.matchData));

const rankComparisonLabel = computed(() => rankComparisonPlatformLabel(matchPlatform.value, props.matchData?.match_type));

const emit = defineEmits([
  'update-selected-user',
  'update-selected-stat',
  'update-user-data',
  'update-user-match-history',
  'updateActiveTab',
  'openServerModal',
]);

function hasMeaningfulMatchStats(player) {
  return (
    player
    && ((player.kills.value && player.kills.value > 0)
      || (player.deaths.value && player.deaths.value > 0)
      || (player.assists.value && player.assists.value > 0)
      || (player.damage.value && player.damage.value > 0))
  );
}

const users = computed(() => {
  const players = props.matchData?.overviews;
  if (!players?.length) return [];
  return [...players]
    .filter(hasMeaningfulMatchStats)
    .sort((a, b) => a.side - b.side);
});

// Ensure the selected user is always one of the filtered users with meaningful stats
watch(
  users,
  (newUsers) => {
    if (!newUsers?.length || !props.selectedUser) return;

    const selectedSteamId = props.selectedUser?.steam_id;

    const isSelectedInFiltered = !!selectedSteamId
      && newUsers.some((player) => player?.steam_id == selectedSteamId);

    if (!isSelectedInFiltered) {
      emit('update-selected-user', newUsers[0]);
    }
  },
  { immediate: true },
);

const getOptimalSortDirection = (columnKey) => {
  // Stats where lower is better (ascending order shows best first)
  const lowerIsBetterStats = [
    'time_to_kill_average',
    'time_to_damage_average',
    'crosshair_placement_average',
    'opening_fails',
    'trade_kill_fails',
    'traded_death_fails',
    'util_flash_friends_flashed',
    'shots_while_stopping_without_counterstrafing',
    'inaccurate_shots_while_counterstrafing',
    'util_lost_cost',
    'util_fire_friendly_burn',
    'average_friend_flash_time',
    'util_flash_total_friend_duration',
    'average_friend_he_damage',
    'util_he_friend_damage',
    'initial_crosshair_placement_average',
    'zero_kill_rounds',
  ];

  return lowerIsBetterStats.includes(columnKey) ? 'asc' : 'desc';
};

const orderedStats = computed(() => {
  if (!props.userData) {
    return [];
  }

  const userStats = props.userData;
  const differences = [];

  // Iterate over the keys in userStats
  for (const key in userStats) {
    const stat = userStats[key];
    if (!stat || typeof stat !== 'object' || !stat.recommended_mods?.length || stat.value == null) {
      continue;
    }
    // Too few attempts: neither a weakness nor a strength.
    if (sampleState(key, userStats, definitions.value)?.belowMin) {
      continue;
    }

    const direction = getOptimalSortDirection(key);
    const higherIsBetter = direction !== 'asc';
    // Skipped when there is no peer average to compare against
    const comparison = compareStatToAverage(stat.value, stat.avg, higherIsBetter);

    if (!comparison) {
      continue;
    }

    const { difference, percentDifference } = comparison;

    differences.push({
      key,
      title: key,
      friendly_title: statFriendlyName(key),
      value: stat.value,
      difference: difference.toFixed(2),
      percentDifference: percentDifference.toFixed(2),
      unit_type: ['time_to_kill_average', 'time_to_damage_average', 'average_enemy_flash_time'].includes(key)
        ? 'seconds'
        : stat.unit_type,
      is_ascending: stat.is_ascending,
      recommended_mods: stat.recommended_mods,
    });
  }

  // Sort the differences to get the stats with the most significant percentage negative impact first
  differences.sort((a, b) => a.percentDifference - b.percentDifference);
  return differences;
});

watch([orderedStats, definitionsSettled], ([stats, settled]) => {
  if (settled && stats[0]?.percentDifference < 0) {
    updateSelectedStat(stats[0]);
  }
}, { immediate: true });

const negativeStats = computed(() => orderedStats.value.filter((stat) => stat.percentDifference < 0));

// No peer average for any stat, so an empty weakness list means there was
// nothing to compare against rather than a clean bill of health.
const rankComparisonUnavailable = computed(() => {
  const comparableStats = Object.values(props.userData || {}).filter(
    (stat) => stat && typeof stat === 'object' && stat.recommended_mods?.length,
  );

  return (
    comparableStats.length > 0
    && comparableStats.every((stat) => !hasRankAverage(stat.avg))
  );
});

const positiveStats = computed(() => orderedStats.value
  .filter((stat) => stat.percentDifference > 0)
  ?.sort((a, b) => b.percentDifference - a.percentDifference));

const playerName = (player) => (
  player?.display_name || t('coach.player_row.unknown_player')
);

function imageURL() {
  return props.selectedUser.avatar || unknownUserImage;
}

function updateServerModal() {
  emit('openServerModal', props.selectedStat);
}

function updateSelectedUser(user) {
  emit('update-selected-user', user);
}

function updateSelectedStat(stat) {
  updateUserStatHistory(stat);
  emit('update-selected-stat', stat);
}

function getPlayerPastStats() {
  if (matchPlatform.value === 'refrag' || matchPlatform.value === 'pro') {
    return;
  }

  const steamId = props.selectedUser?.steam_id;
  if (!steamId) return;

  const params = {
    platform: matchPlatform.value,
    match_type: props.matchData.match_type,
  };
  axios
    .get(`/coach/players/${steamId}/recent_match_stats`, { params })
    .then((response) => {
      const matches = (response.data.matches || []).map((entry) => ({
        ...entry.overview,
        match_id: entry.match.uuid,
        date: entry.match.played_on,
      }));
      emit('update-user-match-history', matches);
    })
    .catch((err) => { console.error('[getPlayerPastStats] failed:', err); });
}

function updateUserStatHistory(stat) {
  const mappedStatHistory = props.userMatchHistory?.map((match) => formatData(match[stat.title], stat.unit_type));

  statAverage.value = formatData(
    props.userData[stat.key].avg,
    stat.unit_type,
  );
  mappedStats.value = mappedStatHistory;
}

function toTitleCase(str) {
  return str
    .split('_') // Split the string by underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
    .join(' '); // Join the words with a space
}

function formatData(val, type) {
  const value = Math.abs(parseFloat(val));
  if (isNaN(value)) return 0;

  switch (type) {
    case 'integer': return parseFloat(value.toFixed(2));
    case 'percentage': return parseFloat((value * 100).toFixed(1));
    case 'decimal': return parseFloat(value.toFixed(2));
    case 'degrees': return parseFloat(value.toFixed(1));
    case 'time':
    case 'seconds':
    case 'miliseconds': return parseFloat(value.toFixed(2));
    default: return value;
  }
}

function formatStatString(val, type) {
  let formattedStat;
  const value = formatData(val, type);

  switch (type) {
    case 'integer':
    case 'decimal':
      return value;
    case 'percentage':
      return `${value}%`;
    case 'degrees':
      return `${value}°`;
    case 'seconds':
    case 'time':
    case 'nanoseconds':
      return `${value}s`;
      break;
    default:
      return value.toString();
  }
}

// Format stat value based on configuration
function formatStatValue(stat) {
  const value = props.selectedUser[stat.key]?.value;

  if (stat.formatter === 'calculated') {
    // Handle calculated values like K/D ratio
    const kills = props.selectedUser.kills.value;
    const deaths = props.selectedUser.deaths.value;
    return (kills / (deaths || 1)).toFixed(2);
  }

  if (value === null || value === undefined) return '-';

  switch (stat.formatter) {
    case 'integer':
      return Math.round(value).toString();
    case 'decimal':
      const places = stat.decimalPlaces || 2;
      return value.toFixed(places);
    case 'percentage':
      const multiplier = stat.multiplier || 1;
      return `${(value * multiplier).toFixed(0)}%`;
    case 'seconds':
    case 'miliseconds':
      return `${value.toFixed(2)}s`;
    case 'currency':
      const prefix = stat.prefix || '';
      return prefix + Math.round(value).toString();
    default:
      return value.toString();
  }
}

// Get color class for a stat based on comparison data
function getStatColorClassForStat(statKey) {
  const average = props.selectedUser[statKey]?.avg;
  if (average === null || average === undefined) return 'text-text-primary';

  const userValue = props.selectedUser[statKey]?.value;
  if (userValue === null || userValue === undefined) return 'text-text-primary';

  const percentageOfAverage = userValue / average;

  const isWorseWhenHigher = isWorseWhenHigherStat(statKey, props.selectedUser[statKey]);

  return statColor(percentageOfAverage, isWorseWhenHigher);
}

const statColor = (percentageOfAverage, isWorseWhenHigher) => {
  if (percentageOfAverage === null || percentageOfAverage === undefined) {
    return 'text-text-primary';
  }

  if (isWorseWhenHigher) {
    if (percentageOfAverage < 0.6) {
      return 'text-success-400';
    } if (percentageOfAverage < 0.8) {
      return 'text-[#9ccc70]';
    } if (percentageOfAverage > 1.4) {
      return 'text-danger-400';
    } if (percentageOfAverage > 1.2) {
      return 'text-warning-400';
    }
  } else {
    if (percentageOfAverage > 1.4) {
      return 'text-success-400';
    } if (percentageOfAverage > 1.15) {
      return 'text-[#9ccc70]';
    } if (percentageOfAverage < 0.6) {
      return 'text-danger-400';
    } if (percentageOfAverage < 0.8) {
      return 'text-yellow-400';
    }
  }

  return 'text-text-primary';
};

// Update the options object
const options = computed(() => ({
  spanGaps: true,
  plugins: {
    legend: {
      display: false, // Hide the legend
    },
    title: {
      display: false, // Hide the title
    },
    clickablePoints: clickablePointsPlugin,
  },
  elements: {
    point: {
      radius: 4,
      hoverRadius: 6,
    },
  },
  scales: {
    x: {
      display: false, // Hide the X-axis labels and grid lines
      grid: {
        color: 'transparent',
        borderColor: 'transparent',
      },
    },
    y: {
      grid: {
        borderDash: [5, 5],
        color: '#37485D',
        borderColor: 'transparent',
      },
      ticks: {
        color: '#9CADC4',
        maxTicksLimit: 5,
      },
      beginAtZero: true, // Start the scale at 0
      suggestedMin: 0, // Ensure the minimum value is always 0 or lower
    },
  },
  maintainAspectRatio: false, // Allow the chart to adjust its size
  responsive: true,
}));

// Update the getChartData function
function getChartData() {
  if (props.userMatchHistory?.length === 0 || !props.selectedStat) {
    return null;
  }

  // Create reversed arrays for labels and data
  const reversedLabels = [...props.userMatchHistory]
    .reverse()
    .map((match) => match.date.split('T')[0]);
  const reversedData = [...props.userMatchHistory]
    .reverse()
    .map((match) => formatData(match[props.selectedStat.title], props.selectedStat.unit_type));

  // Calculate the maximum value from both datasets
  const maxValue = Math.max(...reversedData, statAverage.value);

  // Calculate a buffer (20% higher than the max value)
  const buffer = maxValue * 0.2;

  return {
    labels: reversedLabels,
    datasets: [
      {
        data: reversedData,
        fill: true,
        backgroundColor: '#6B70D115',
        borderColor: '#6B70D1',
        tension: 0,
        pointRadius: 3,
        pointHoverBackgroundColor: '#6B70D1',
        pointHoverBorderColor: '#FFFFFF',
      },
      {
        data: Array(props.userMatchHistory.length).fill(statAverage.value),
        fill: false,
        borderColor: '#C44CB6',
        borderDash: [5, 5],
        pointRadius: 0,
        borderWidth: 1,
      },
    ],
  };
}

// When user switches (or on mount), update userData from match overview and fetch their match history for charts.
watch(
  () => props.selectedUser,
  (newVal, oldVal) => {
    if (newVal?.steam_id == oldVal?.steam_id) return;
    if (newVal) {
      emit('update-user-data', newVal);
      getPlayerPastStats();
    }
  },
  { immediate: true },
);

watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId !== oldId) {
      getPlayerPastStats();
      emit('get-match-data');
    }
  },
);
</script>
